/* eslint-disable react/no-array-index-key */
// Index keys are intentional here: rows map 1:1 to positional final-form
// paths (vip_windows[i]), so the key must match the path index. Rows are
// only appended/removed, never reordered.
import React from "react";
import { Field } from "react-final-form";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import { ExlyDropdown, ExlyInput, ExlyTimePicker } from "common/form";
import ExlyButtonToggle from "common/Components/ExlyButtonToggle";
import CopyTime from "schedule-v2/modals/CopyTime";
import { AddTierButton, TierDeleteButton } from "./OfferTierActions";
import { ArrayLevelError } from "./ArrayLevelError";
import {
  ARRAY_ERROR_KEYS,
  DEFAULT_VIP_APPLY_BY,
  LTO_FORM_KEYS,
  VALUE_UNIT,
  VIP_APPLY_BY,
} from "../constants";
import {
  addRangeAt,
  addRow,
  asArray,
  copyVipRowTo,
  emptyVipRow,
  emptyWeekdayRow,
  removeRangeAt,
  removeRowAt,
} from "../utils/tierOps";
import { OFFER_DIRECTION } from "../utils/unifiedOffer";
import { validateFlatFinalPrice } from "../limitedOfferValidators";

const WEEKDAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const VALUE_UNIT_OPTIONS = [
  { label: "%", value: VALUE_UNIT.PERCENT },
  { label: "₹ Flat", value: VALUE_UNIT.FLAT },
];

/**
 * VIP / Priority Slots (§5.3). Availability-style builder.
 * Row = weekday/date + N time ranges + one markup. Copy modal overwrites targets.
 * structuralDisabled locks shape (apply-by, ranges add/remove, dates, copy)
 * on edit; disabled locks values (times, markups, vip dates).
 */
const vipWindowsOf = (values, fieldPrefix = LTO_FORM_KEYS.VIP_WINDOWS) =>
  asArray(values?.[fieldPrefix]);
const hasActiveVipRow = (rows) =>
  asArray(rows).some((r) => asArray(r.ranges).length > 0);

const VipSlotsBuilder = ({
  values = {},
  form,
  showInternational = false,
  disabled = false,
  structuralDisabled = false,
  originalPrice = null,
}) => {
  const fieldPrefix = LTO_FORM_KEYS.VIP_WINDOWS;
  const applyBy = values[LTO_FORM_KEYS.VIP_APPLY_BY] || DEFAULT_VIP_APPLY_BY;
  const shapeLocked = disabled || structuralDisabled;
  const isFlat = values[LTO_FORM_KEYS.VALUE_UNIT] === VALUE_UNIT.FLAT;
  const isSurge = values[LTO_FORM_KEYS.DIRECTION] === OFFER_DIRECTION.SURGE;
  const markupSymbol = isFlat ? "₹" : "%";
  const valueLabel = isFlat ? "Price" : "Markup";
  const storedRows = asArray(form?.getState?.().values?.[fieldPrefix]);
  const rows = storedRows.length
    ? storedRows
    : applyBy === VIP_APPLY_BY.WEEKDAY
    ? WEEKDAYS.map(emptyWeekdayRow)
    : [];
  // Base for mutations must include the local weekday defaults, otherwise the
  // very first add writes into an empty form array and is lost.
  const currentRows = () =>
    storedRows.length
      ? asArray(form?.getState?.().values?.[fieldPrefix])
      : rows;
  const [copyFrom, setCopyFrom] = React.useState(null);

  const isWeekdayMode = applyBy === VIP_APPLY_BY.WEEKDAY;

  const setRows = (next) => form.change(fieldPrefix, asArray(next));

  // Seed the seven weekday rows into form state so the form value
  // matches what's rendered (and array-level validation sees the empty rows).
  React.useEffect(() => {
    if (shapeLocked) return;
    const stored = asArray(form?.getState?.().values?.[fieldPrefix]);
    if (
      !stored.length &&
      (values[LTO_FORM_KEYS.VIP_APPLY_BY] || DEFAULT_VIP_APPLY_BY) ===
        VIP_APPLY_BY.WEEKDAY
    ) {
      form.change(fieldPrefix, WEEKDAYS.map(emptyWeekdayRow));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="tw-w-full">
      <span className="tw-block tw-text-[13px] tw-font-semibold tw-mb-2">
        Priority Windows *
      </span>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4 tw-mb-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-[12px] tw-font-semibold tw-text-gray-500">
            Apply by
          </span>
          {/* key remounts the shared toggle when the form value changes:
              it snapshots `selected` into internal state only once, so
              without this an edit-prefilled value would show stale. */}
          <div
            key={applyBy}
            className={
              shapeLocked ? "tw-pointer-events-none tw-opacity-40" : undefined
            }
            title={shapeLocked ? "Locked in edit mode" : undefined}
          >
            <ExlyButtonToggle
              switches={["Day of Week", "Specific Date"]}
              selected={isWeekdayMode ? 0 : 1}
              onClick={(e, index) => {
                const next =
                  index === 0 ? VIP_APPLY_BY.WEEKDAY : VIP_APPLY_BY.DATE;
                form.change(LTO_FORM_KEYS.VIP_APPLY_BY, next);
                setRows(
                  next === VIP_APPLY_BY.WEEKDAY
                    ? WEEKDAYS.map(emptyWeekdayRow)
                    : []
                );
              }}
            />
          </div>
        </div>
        {/* value_unit is driven by Offer Type (Amount → ₹ Flat, Percentage → %).
            Hidden Field keeps the backend payload identical — no UI to change it. */}
        <div style={{ display: "none" }}>
          <Field
            name={LTO_FORM_KEYS.VALUE_UNIT}
            component={ExlyDropdown}
            options={VALUE_UNIT_OPTIONS}
            disabled={shapeLocked}
            size="small"
          />
        </div>
      </div>

      {applyBy === VIP_APPLY_BY.WEEKDAY && (
        <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-3">
          <Field
            name={LTO_FORM_KEYS.VIP_START_DATE}
            component={ExlyInput}
            type="date"
            label="Start Date *"
            fullWidth
            disabled={disabled}
            validate={(v) =>
              hasActiveVipRow(vipWindowsOf(values)) && !v
                ? "Required"
                : undefined
            }
          />
          <Field
            name={LTO_FORM_KEYS.VIP_END_DATE}
            component={ExlyInput}
            type="date"
            label="End Date (Optional)"
            placeholder="Eg, Ending Date"
            fullWidth
            disabled={disabled}
          />
        </div>
      )}
      {applyBy === VIP_APPLY_BY.WEEKDAY && (
        <p className="tw-text-[12px] tw-text-gray-500 tw-mb-3">
          This weekly pattern repeats from this date.
        </p>
      )}

      <div className="tw-flex tw-flex-col tw-gap-2">
        {rows.map((row, idx) => (
          <div
            key={`vip-row-${fieldPrefix}-${idx}`}
            className="tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-p-2"
          >
            <div className="tw-flex tw-items-center tw-gap-2">
              {applyBy === VIP_APPLY_BY.DATE ? (
                <>
                  <div className="tw-flex-1 tw-min-w-0">
                    <Field
                      name={`${fieldPrefix}[${idx}].label`}
                      component={ExlyInput}
                      type="date"
                      placeholder="Select date"
                      disabled={disabled}
                      fullWidth
                    />
                  </div>
                  <TierDeleteButton
                    disabled={shapeLocked}
                    onClick={() => setRows(removeRowAt(currentRows(), idx))}
                    label="Remove date"
                  />
                </>
              ) : (
                <span className="tw-text-[14px] tw-font-bold tw-flex-1">
                  {row.label}
                </span>
              )}
            </div>
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-2">
              {row.ranges.map((range, rIdx) => (
                <div
                  key={`vip-range-${fieldPrefix}-${idx}-${rIdx}`}
                  className="tw-flex tw-items-center tw-gap-2"
                >
                  <div className="tw-flex-1 tw-min-w-0">
                    <Field
                      component={ExlyTimePicker}
                      name={`${fieldPrefix}[${idx}].ranges[${rIdx}].start`}
                      desktop_width="120px"
                      native_width="120px"
                      placeholder="Start Time"
                      uniqueId={`vip_${fieldPrefix}_${idx}_${rIdx}_start`}
                      disabled={disabled}
                    />
                  </div>
                  <div className="tw-flex-1 tw-min-w-0">
                    <Field
                      component={ExlyTimePicker}
                      name={`${fieldPrefix}[${idx}].ranges[${rIdx}].end`}
                      desktop_width="120px"
                      native_width="120px"
                      placeholder="End Time"
                      uniqueId={`vip_${fieldPrefix}_${idx}_${rIdx}_end`}
                      disabled={disabled}
                    />
                  </div>
                  <TierDeleteButton
                    disabled={shapeLocked}
                    onClick={() =>
                      setRows(removeRangeAt(currentRows(), idx, rIdx))
                    }
                    label="Remove range"
                  />
                </div>
              ))}
              {row.ranges.length === 0 && (
                <p className="tw-text-[12px] tw-italic tw-text-gray-400">
                  No priority time set for this{" "}
                  {applyBy === VIP_APPLY_BY.WEEKDAY ? "day" : "date"}
                </p>
              )}
            </div>
            <div className="tw-mt-2 tw-flex tw-items-center tw-gap-1">
              <IconButton
                size="small"
                disabled={shapeLocked}
                title="Add time range"
                onClick={() => setRows(addRangeAt(currentRows(), idx))}
                className="tw-text-gray-500 hover:tw-text-purple-700 disabled:tw-opacity-40"
              >
                <AddIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={shapeLocked}
                title="Copy times to..."
                onClick={() => setCopyFrom(idx)}
                className="tw-text-gray-500 hover:tw-text-purple-700 disabled:tw-opacity-40"
              >
                <FileCopyOutlinedIcon fontSize="small" />
              </IconButton>
              {row.ranges.length > 0 && (
                <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
                  <span className="tw-text-[12px] tw-font-semibold tw-text-gray-500">
                    {valueLabel}
                  </span>
                  <div className="tw-w-[100px]">
                    <Field
                      name={`${fieldPrefix}[${idx}].markup`}
                      component={ExlyInput}
                      type="number"
                      min="0"
                      fullWidth
                      disabled={disabled}
                      validate={(value) =>
                        isFlat
                          ? validateFlatFinalPrice(value, originalPrice, {
                              isSurge,
                            })
                          : value === "" ||
                            value === null ||
                            value === undefined
                          ? "Required"
                          : undefined
                      }
                    />
                  </div>
                  <span
                    data-testid={`${fieldPrefix}[${idx}].unit_badge`}
                    className="tw-text-[13px] tw-font-bold tw-text-purple-700"
                  >
                    {markupSymbol}
                  </span>
                  {showInternational ? (
                    <>
                      <span className="tw-text-[12px] tw-font-semibold tw-text-gray-500">
                        Intl ($)
                      </span>
                      <div className="tw-w-[100px]">
                        <Field
                          name={`${fieldPrefix}[${idx}].markup_international`}
                          component={ExlyInput}
                          type="number"
                          min="0"
                          fullWidth
                          disabled={disabled}
                        />
                      </div>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {applyBy === VIP_APPLY_BY.DATE && (
        <AddTierButton
          disabled={shapeLocked}
          onClick={() => setRows(addRow(currentRows(), emptyVipRow))}
        >
          + Add another date
        </AddTierButton>
      )}
      <p className="tw-mt-3 tw-text-[12px] tw-text-gray-500">
        Add one or more time ranges to a day (or date), set its{" "}
        {isFlat ? "price" : "markup"}, then use the copy icon to apply the same
        ranges and {isFlat ? "price" : "markup"} to other rows instead of
        re-entering them.
      </p>
      <ArrayLevelError errorKey={ARRAY_ERROR_KEYS.VIP} />

      <CopyTime
        weekdays={
          isWeekdayMode
            ? [1, 2, 3, 4, 5, 6, 7]
            : rows.map((r) => r.label).filter(Boolean)
        }
        showDates={!isWeekdayMode}
        copyFrom={
          isWeekdayMode
            ? copyFrom === null
              ? null
              : copyFrom + 1
            : rows[copyFrom]?.label
        }
        isOpen={copyFrom !== null}
        onClose={() => setCopyFrom(null)}
        onCopy={(selectedDays) => {
          if (copyFrom === null) return;
          const toIndex = isWeekdayMode
            ? (d) => Number(d) - 1
            : (date) => rows.findIndex((r) => r.label === date);
          const targets = selectedDays
            .map(toIndex)
            .filter((i) => i >= 0 && i !== copyFrom);
          setRows(copyVipRowTo(currentRows(), copyFrom, targets));
          setCopyFrom(null);
        }}
      />
    </div>
  );
};

export { VipSlotsBuilder };
